/**
 * Clinician Plan Trial Modal
 *
 * Shown to users who have access to a 14-day free trial of the Clinician plan.
 * "Enjoy 14 days free of the Clinician plan!"
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7454-168922&m=dev
 */

import { usePaywallStates } from '@/atoms/paywallAtoms';
import {
  CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY,
  GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT,
  IS_CLINICIAN_TRIAL_MODAL_EXPERIMENT_ASSIGNED,
} from '@/components/constants/flags';
import { isDictationPremiumTrialEligibleAtom } from '@/components/modal/dictation-premium-trial-modal/atoms';
import type { Feature } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { TrialStartModalBase } from '@/components/modal/trial-start-modal-base';
import { useIsHeidi2OptInEnabled } from '@/features/flags';
import { useManualExposure } from '@/hooks/amplitude';
import { useMutateActivateClinicianTrial } from '@/hooks/mutation/useMutateActivateClinicianTrial';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { Button } from '@heidi/design-system/button';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { BookOpen, FileText, ListChecks } from 'lucide-react';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  clinicianTrialStartMessages,
  heidi2ClinicianTrialMessages,
} from './clinician-trial-modal.messages';

/** Clinician trial length used in the Heidi 2 copy; matches the legacy 14-day strings. */
const HEIDI2_TRIAL_DAYS = 14;

/* ─────────────────────────────────────────────────────────────────────────────
 * Main Component
 * ───────────────────────────────────────────────────────────────────────────── */
export const ClinicianTrialModal = () => {
  const intl = useIntl();
  const {
    isOpen: isQueueOpen,
    handleClose: handleQueueClose,
    dismissible: queueDismissible,
  } = useQueuedModal(ModalId.CLINICIAN_TRIAL);

  const clinicianTrialModalExperimentVariant =
    GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT();
  const isClinicianTrialModalExperimentAssigned =
    IS_CLINICIAN_TRIAL_MODAL_EXPERIMENT_ASSIGNED();

  const { clinicianTrialModalOpen, toggleClinicianTrialModal } = usePaywallStates();
  const { clinicianTrialStartFeatures } = useFeatureLists();

  const showHeidi2Design = useIsHeidi2OptInEnabled();

  const heidi2Features = useMemo<Feature[]>(
    () => [
      {
        id: 'templates',
        icon: FileText,
        description: intl.formatMessage(heidi2ClinicianTrialMessages.templatesFeature),
      },
      {
        id: 'evidence',
        icon: BookOpen,
        description: intl.formatMessage(heidi2ClinicianTrialMessages.evidenceFeature),
      },
      {
        id: 'admin-tasks',
        icon: ListChecks,
        description: intl.formatMessage(heidi2ClinicianTrialMessages.adminTasksFeature),
      },
    ],
    [intl]
  );

  const isDictationPremiumTrialEligible = useAtomValue(
    isDictationPremiumTrialEligibleAtom
  );

  // Modal is open if either queued modal is open OR atom-controlled modal is open
  const isOpen = isQueueOpen || clinicianTrialModalOpen;
  // Use queue dismissible if queued modal is open, otherwise allow dismissal
  const dismissible = isQueueOpen ? queueDismissible : true;

  // only record exposure if user is actually part of the experiment release group
  const isClinicianTrialModalManualExposureVisible =
    isClinicianTrialModalExperimentAssigned &&
    (clinicianTrialModalExperimentVariant === 'NONE' || isOpen);

  useManualExposure({
    flagKey: CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY,
    isEnabled: isClinicianTrialModalExperimentAssigned,
    isVisible: isClinicianTrialModalManualExposureVisible,
    ...(isClinicianTrialModalExperimentAssigned
      ? { variant: clinicianTrialModalExperimentVariant }
      : {}),
  });

  const isOptionalVariant = clinicianTrialModalExperimentVariant === 'OPTIONAL';

  useEffect(() => {
    if (isOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: {
          screen_name: ScreenName.ClinicianTrialModal,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { mutate: activateClinicianTrial, isPending } = useMutateActivateClinicianTrial();

  const handlePrimaryClick = useCallback(() => {
    activateClinicianTrial(undefined, {
      onSettled: () => {
        if (isQueueOpen) {
          handleQueueClose();
        }
        toggleClinicianTrialModal(false);
      },
    });
  }, [isQueueOpen, handleQueueClose, toggleClinicianTrialModal, activateClinicianTrial]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        if (isQueueOpen) {
          handleQueueClose();
        }
        toggleClinicianTrialModal(false);
      }
    },
    [isQueueOpen, handleQueueClose, toggleClinicianTrialModal]
  );

  const subtitle = useMemo(() => {
    if (isOptionalVariant) {
      return intl.formatMessage(clinicianTrialStartMessages.subtitleOptional, {
        bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
      });
    }
    return intl.formatMessage(clinicianTrialStartMessages.subtitle);
  }, [intl, isOptionalVariant]);

  const handleExploreLater = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.ClinicianTrialModal,
        action: ButtonAction.TrialExploreLater,
        label: intl.formatMessage(clinicianTrialStartMessages.exploreLater),
      },
    });
    handleOpenChange(false);
  }, [handleOpenChange, intl]);

  if (
    isAppleCompliantBuild() ||
    (isDictationPremiumTrialEligible && !clinicianTrialModalOpen)
  ) {
    return null;
  }

  // Keep Explore later on both designs so OPTIONAL does not collapse into CONTROL under Heidi 2.
  const exploreLaterButton = isOptionalVariant ? (
    <Button className="w-full" variant="ghost" size="lg" onClick={handleExploreLater}>
      {intl.formatMessage(clinicianTrialStartMessages.exploreLater)}
    </Button>
  ) : undefined;

  const variantProps = showHeidi2Design
    ? {
        title: intl.formatMessage(heidi2ClinicianTrialMessages.title, {
          trialDays: HEIDI2_TRIAL_DAYS,
        }),
        subtitle: intl.formatMessage(heidi2ClinicianTrialMessages.subtitle),
        features: heidi2Features,
        primaryButtonText: intl.formatMessage(heidi2ClinicianTrialMessages.confirmButton),
        footerText: intl.formatMessage(heidi2ClinicianTrialMessages.footer, {
          trialDays: HEIDI2_TRIAL_DAYS,
        }),
        hidePrimaryButtonIcon: true,
        footerTypography: 'footnote' as const,
        secondaryButton: exploreLaterButton,
      }
    : {
        title: intl.formatMessage(clinicianTrialStartMessages.title),
        subtitle,
        features: clinicianTrialStartFeatures,
        primaryButtonText: intl.formatMessage(clinicianTrialStartMessages.cta),
        headerIcon: 'gift' as const,
        secondaryButton: exploreLaterButton,
      };

  return (
    <TrialStartModalBase
      open={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      hideCloseIcon
      onPrimaryClick={handlePrimaryClick}
      isPrimaryLoading={isPending}
      {...variantProps}
    />
  );
};
